/**
 * Materials Service
 * 
 * Handles all CRUD operations and business logic for raw materials/ingredients.
 * This service provides a clean API for material management and ensures
 * consistent data handling across the application.
 * 
 * Features:
 * - CRUD operations for materials
 * - Stock adjustment tracking
 * - Cost history management
 * - Low stock alerts
 * - Barcode support
 * 
 * @module inventory/services/MaterialsService
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  Material,
  MaterialWithStatus,
  MaterialCostHistory,
  CreateMaterialDTO,
  UpdateMaterialDTO,
  Transaction,
  CreateTransactionDTO,
  StockStatus,
  UUID,
} from '../types';

/**
 * Input Validation Helper
 */
class InputValidator {
  static validateQuantity(quantity: number, allowNegative: boolean = false): void {
    if (typeof quantity !== 'number' || isNaN(quantity)) {
      throw new Error('Quantity must be a valid number');
    }
    if (!allowNegative && quantity < 0) {
      throw new Error('Quantity cannot be negative');
    }
  }

  static validatePrice(price: number): void {
    if (typeof price !== 'number' || isNaN(price)) {
      throw new Error('Price must be a valid number');
    }
    if (price < 0) {
      throw new Error('Price cannot be negative');
    }
  }

  static validateId(id: string, fieldName: string = 'ID'): void {
    if (!id || typeof id !== 'string' || id.trim().length === 0) {
      throw new Error(`${fieldName} is required`);
    }
  }
}

export class MaterialsService {
  /**
   * Get all materials for a branch
   * 
   * @param branchId - Branch ID to filter by
   * @param includeInactive - Whether to include inactive materials
   * @returns Array of materials
   */
  async getMaterials(
    branchId: UUID,
    includeInactive: boolean = false
  ): Promise<Material[]> {
    InputValidator.validateId(branchId, 'Branch ID');

    const query = supabase
      .from('raw_materials')
      .select(`
        id,
        name,
        branch_id,
        user_id,
        unit_type,
        current_stock,
        min_threshold,
        reorder_point,
        max_stock,
        cost_per_unit,
        supplier,
        storage_location,
        barcode,
        is_active,
        created_at,
        updated_at
      `)
      .eq('branch_id', branchId)
      .order('name');

    // Note: is_active filtering removed - column doesn't exist in schema
    // Filter inactive materials on client side if needed

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching materials:', error);
      throw new Error(`Failed to fetch materials: ${error.message}`);
    }

    return data as Material[];
  }

  /**
   * Get materials with stock status
   * 
   * @param branchId - Branch ID to filter by
   * @returns Materials with calculated status
   */
  async getMaterialsWithStatus(branchId: UUID): Promise<MaterialWithStatus[]> {
    const materials = await this.getMaterials(branchId);

    return materials.map(material => ({
      ...material,
      stock_status: this.getStockStatus(material),
      stock_percentage: this.getStockPercentage(material),
      total_value: material.current_stock * material.cost_per_unit,
      days_until_reorder: this.calculateDaysUntilReorder(material),
    }));
  }

  /**
   * Get a single material by ID
   * 
   * @param id - Material ID
   * @returns Material or null
   */
  async getMaterialById(id: UUID): Promise<Material | null> {
    const { data, error } = await supabase
      .from('raw_materials')
      .select(`
        id,
        name,
        branch_id,
        user_id,
        unit_type,
        current_stock,
        min_threshold,
        reorder_point,
        max_stock,
        cost_per_unit,
        supplier,
        storage_location,
        barcode,
        is_active,
        created_at,
        updated_at
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching material:', error);
      return null;
    }

    return data as Material;
  }

  /**
   * Get material by barcode
   * 
   * @param barcode - Barcode to search for
   * @param branchId - Optional branch ID filter
   * @returns Material or null
   */
  async getMaterialByBarcode(
    barcode: string,
    branchId?: UUID
  ): Promise<Material | null> {
    let query = supabase
      .from('raw_materials')
      .select(`
        id,
        name,
        branch_id,
        user_id,
        unit_type,
        current_stock,
        min_threshold,
        reorder_point,
        max_stock,
        cost_per_unit,
        supplier,
        storage_location,
        barcode,
        is_active,
        created_at,
        updated_at
      `)
      .eq('barcode', barcode);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error('Error fetching material by barcode:', error);
      return null;
    }

    return data as Material | null;
  }

  /**
   * Create a new material
   * 
   * @param material - Material data
   * @returns Created material
   */
  async createMaterial(material: CreateMaterialDTO): Promise<Material> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('raw_materials')
      .insert({
        ...material,
        user_id: userData.user.id,
        is_active: true,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating material:', error);
      throw new Error(`Failed to create material: ${error.message}`);
    }

    return data as Material;
  }

  /**
   * Update an existing material
   * 
   * @param id - Material ID
   * @param updates - Fields to update
   * @returns Updated material
   */
  async updateMaterial(id: UUID, updates: UpdateMaterialDTO): Promise<Material> {
    const { data, error } = await supabase
      .from('raw_materials')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating material:', error);
      throw new Error(`Failed to update material: ${error.message}`);
    }

    return data as Material;
  }

  /**
   * Update material cost (automatically tracked in cost history)
   * 
   * @param id - Material ID
   * @param newCost - New cost per unit
   * @param reason - Reason for cost change
   * @returns Updated material
   */
  async updateMaterialCost(
    id: UUID,
    newCost: number,
    reason?: string
  ): Promise<Material> {
    // The trigger will automatically create cost history
    const material = await this.updateMaterial(id, { cost_per_unit: newCost });
    
    // Optionally update the reason in cost history
    if (reason) {
      await supabase
        .from('material_cost_history')
        .update({ change_reason: reason })
        .eq('material_id', id)
        .is('effective_to', null);
    }

    return material;
  }

  /**
   * Soft delete a material (set is_active = false)
   * 
   * @param id - Material ID
   * @returns Deleted material
   */
  async deleteMaterial(id: UUID): Promise<Material> {
    return this.updateMaterial(id, { is_active: false });
  }

  /**
   * Hard delete a material (permanent)
   * 
   * @param id - Material ID
   */
  async permanentlyDeleteMaterial(id: UUID): Promise<void> {
    const { error } = await supabase
      .from('raw_materials')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting material:', error);
      throw new Error(`Failed to delete material: ${error.message}`);
    }
  }

  /**
   * Adjust stock quantity
   * 
   * @param materialId - Material ID
   * @param branchId - Branch ID
   * @param quantity - Quantity to add (positive) or subtract (negative)
   * @param reason - Reason for adjustment
   * @param notes - Optional notes
   * @returns Created transaction
   */
  async adjustStock(
    materialId: UUID,
    branchId: UUID,
    quantity: number,
    reason: 'add' | 'subtract' | 'waste',
    notes?: string
  ): Promise<Transaction> {
    // Validate inputs
    InputValidator.validateId(materialId, 'Material ID');
    InputValidator.validateId(branchId, 'Branch ID');
    InputValidator.validateQuantity(quantity, true); // Allow negative for subtract

    const material = await this.getMaterialById(materialId);
    if (!material) {
      throw new Error('Material not found');
    }

    // Determine transaction type
    let transactionType: CreateTransactionDTO['transaction_type'];
    if (reason === 'waste') {
      transactionType = 'waste';
    } else if (quantity > 0) {
      transactionType = 'adjustment_add';
    } else {
      transactionType = 'adjustment_subtract';
    }

    // Create transaction
    const transaction = await this.createTransaction({
      material_id: materialId,
      branch_id: branchId,
      transaction_type: transactionType,
      quantity: Math.abs(quantity),
      cost_per_unit: material.cost_per_unit,
      notes,
    });

    // Update material stock
    const newStock = material.current_stock + quantity;
    await this.updateMaterial(materialId, { current_stock: Math.max(0, newStock) });

    return transaction;
  }

  /**
   * Restock material (purchase from supplier)
   * 
   * @param materialId - Material ID
   * @param branchId - Branch ID
   * @param quantity - Quantity to add
   * @param costPerUnit - Cost per unit for this purchase
   * @param notes - Optional notes
   * @returns Created transaction
   */
  async restock(
    materialId: UUID,
    branchId: UUID,
    quantity: number,
    costPerUnit: number,
    notes?: string
  ): Promise<Transaction> {
    // Validate inputs
    InputValidator.validateId(materialId, 'Material ID');
    InputValidator.validateId(branchId, 'Branch ID');
    InputValidator.validateQuantity(quantity);
    InputValidator.validatePrice(costPerUnit);

    const material = await this.getMaterialById(materialId);
    if (!material) {
      throw new Error('Material not found');
    }

    // Create restock transaction
    const transaction = await this.createTransaction({
      material_id: materialId,
      branch_id: branchId,
      transaction_type: 'restock',
      quantity,
      cost_per_unit: costPerUnit,
      notes,
    });

    // Update material stock
    const newStock = material.current_stock + quantity;
    await this.updateMaterial(materialId, { current_stock: newStock });

    // If cost changed significantly, update material cost
    if (Math.abs(costPerUnit - material.cost_per_unit) / material.cost_per_unit > 0.1) {
      await this.updateMaterialCost(materialId, costPerUnit, 'Updated from restock purchase');
    }

    return transaction;
  }

  /**
   * Create an inventory transaction
   * 
   * @param transaction - Transaction data
   * @returns Created transaction
   */
  async createTransaction(transaction: CreateTransactionDTO): Promise<Transaction> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      throw new Error('User not authenticated');
    }

    // Get material info
    const material = await this.getMaterialById(transaction.material_id);
    if (!material) {
      throw new Error('Material not found');
    }

    const { data, error } = await supabase
      .from('inventory_transactions')
      .insert({
        ...transaction,
        user_id: userData.user.id,
        unit: material.unit_type,
        performed_by: userData.user.id,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating transaction:', error);
      throw new Error(`Failed to create transaction: ${error.message}`);
    }

    return data as Transaction;
  }

  /**
   * Get cost history for a material
   * 
   * @param materialId - Material ID
   * @returns Array of cost history records
   */
  async getCostHistory(materialId: UUID): Promise<MaterialCostHistory[]> {
    const { data, error } = await supabase
      .from('material_cost_history')
      .select(`
        id,
        material_id,
        branch_id,
        old_cost,
        new_cost,
        change_reason,
        effective_from,
        effective_to,
        created_at
      `)
      .eq('material_id', materialId)
      .order('effective_from', { ascending: false });

    if (error) {
      console.error('Error fetching cost history:', error);
      throw new Error(`Failed to fetch cost history: ${error.message}`);
    }

    return data as MaterialCostHistory[];
  }

  /**
   * Get low stock materials
   * 
   * @param branchId - Branch ID
   * @returns Materials with low stock
   */
  async getLowStockMaterials(branchId: UUID): Promise<MaterialWithStatus[]> {
    const materials = await this.getMaterialsWithStatus(branchId);
    return materials.filter(m => m.stock_status === 'low-stock' || m.stock_status === 'out-of-stock');
  }

  /**
   * Get materials by supplier
   * 
   * @param branchId - Branch ID
   * @param supplier - Supplier name
   * @returns Materials from supplier
   */
  async getMaterialsBySupplier(branchId: UUID, supplier: string): Promise<Material[]> {
    const { data, error } = await supabase
      .from('raw_materials')
      .select('*')
      .eq('branch_id', branchId)
      .eq('supplier', supplier)
      .order('name');

    if (error) {
      console.error('Error fetching materials by supplier:', error);
      throw new Error(`Failed to fetch materials: ${error.message}`);
    }

    return data as Material[];
  }

  /**
   * Calculate stock status
   */
  private getStockStatus(material: Material): StockStatus {
    if (material.current_stock <= 0) return 'out-of-stock';
    if (material.current_stock <= material.min_threshold) return 'low-stock';
    return 'in-stock';
  }

  /**
   * Calculate stock percentage
   */
  private getStockPercentage(material: Material): number {
    if (material.min_threshold === 0) return 100;
    return Math.min((material.current_stock / material.min_threshold) * 100, 100);
  }

  /**
   * Calculate days until reorder needed
   */
  private calculateDaysUntilReorder(material: Material): number | undefined {
    // This would require transaction history to calculate average daily usage
    // For now, return undefined - will be implemented when integrating with transactions
    return undefined;
  }
}

// Export singleton instance
export const materialsService = new MaterialsService();
